import { Clock } from "lucide-react";
import { CanAccess, useGetList, useGetMany, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Deal, Sale } from "../types";
import {
  daysSinceActivity,
  selectStaleDeals,
  staleActivityCutoff,
} from "./staleDeals";

export const StaleDeals = () => (
  <CanAccess resource="sales" action="list">
    <StaleDealsList />
  </CanAccess>
);

const StaleDealsList = () => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const [now] = useState(() => new Date());
  const { data, isPending } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "last_activity_at", order: "ASC" },
    filter: {
      "archived_at@is": null,
      "last_activity_at@lte": staleActivityCutoff(now),
    },
  });
  const deals = selectStaleDeals(data ?? [], now);
  const companyIds = [...new Set(deals.map((deal) => deal.company_id))];
  const salesIds = [...new Set(deals.map((deal) => deal.sales_id))];
  const { data: companies, isPending: companiesPending } = useGetMany<Company>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );
  const { data: sales, isPending: salesPending } = useGetMany<Sale>(
    "sales",
    { ids: salesIds },
    { enabled: salesIds.length > 0 },
  );
  const waitingForNames =
    deals.length > 0 && (companiesPending || salesPending);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Clock className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.dashboard.stale_deals", {
            _: "Propostas paradas",
          })}
        </h2>
      </div>
      <Card className="py-0">
        {isPending || waitingForNames ? null : deals.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {translate("crm.dashboard.stale_empty", {
              _: "Nenhuma proposta parada",
            })}
          </p>
        ) : (
          <ul>
            {deals.map((deal) => {
              const company = companies?.find(
                (item) => item.id === deal.company_id,
              );
              const sale = sales?.find((item) => item.id === deal.sales_id);
              const details = [
                company?.name,
                sale ? `${sale.first_name} ${sale.last_name}` : undefined,
                findDealLabel(dealStages, deal.stage) ?? deal.stage,
                translate("crm.dashboard.stale_for", {
                  smart_count: daysSinceActivity(deal.last_activity_at, now),
                  _: "parada há %{smart_count} dias",
                }),
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <li key={deal.id}>
                  <Link
                    to={`/deals/${deal.id}/show`}
                    className="block w-full p-3 hover:bg-muted focus:bg-muted focus:outline-none"
                  >
                    <div className="text-sm font-medium">{deal.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {details}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};
